import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Keeps the "1 / N" counter in sync with the Full Look product slideshow,
 * and links the image points (from the Full Look metaobject X/Y fields) to the slides.
 *
 * @typedef {object} Refs
 * @property {import('slideshow').Slideshow} slideshow
 * @property {HTMLElement} [currentIndex]
 * @property {HTMLElement} [totalCount]
 *
 * @extends {Component<Refs>}
 */
export class ProductFullLookSection extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handlePointClick);

    requestAnimationFrame(() => {
      const slideshow = this.#getSlideshow();
      if (!slideshow) return;

      slideshow.setAttribute('actioned', '');
      slideshow.updatedCallback?.();
      slideshow.ensureControls?.();
      slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);

      const count = this.#getSlides().length;
      if (this.refs.totalCount) this.refs.totalCount.textContent = String(count);

      this.#mapPointsToSlides();
      this.#setCurrent(0);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handlePointClick);
    this.#getSlideshow()?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
  }

  /**
   * Slide to the product of the clicked point.
   * @param {Event} event
   */
  #handlePointClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const point = /** @type {HTMLElement | null} */ (target?.closest('.full-look-point') ?? null);
    if (!point || !this.contains(point)) return;

    event.preventDefault();

    const index = Number(point.dataset.slideIndex);
    if (Number.isNaN(index)) return;

    this.#getSlideshow()?.select(index, undefined, { animate: true });
    this.#setCurrent(index);
  };

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSelect = (event) => {
    this.#setCurrent(event.detail.index);
  };

  /**
   * Resolve each point's slide index from its product handle,
   * and hide points whose product is not in the slider.
   */
  #mapPointsToSlides() {
    /** @type {Map<string, number>} */
    const indexByHandle = new Map();

    this.#getSlides().forEach((slide, index) => {
      const el = /** @type {HTMLElement | null} */ (slide.querySelector('[data-product-handle]'));
      const handle = el?.dataset.productHandle;
      if (handle && !indexByHandle.has(handle)) indexByHandle.set(handle, index);
    });

    this.#getPoints().forEach((point) => {
      const index = indexByHandle.get(point.dataset.productHandle ?? '');

      if (index === undefined) {
        point.hidden = true;
        return;
      }

      point.hidden = false;
      point.dataset.slideIndex = String(index);
    });
  }

  /**
   * @param {number} index
   */
  #setCurrent(index) {
    if (this.refs.currentIndex) this.refs.currentIndex.textContent = String(index + 1);

    this.#getPoints().forEach((point) => {
      const isActive = Number(point.dataset.slideIndex) === index;
      point.toggleAttribute('data-active', isActive);
      point.setAttribute('aria-current', String(isActive));
    });
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getPoints() {
    return Array.from(this.querySelectorAll('.full-look-point'));
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSlides() {
    return Array.from(this.#getSlideshow()?.querySelectorAll('slideshow-slides > slideshow-slide') ?? []);
  }

  /**
   * @returns {import('slideshow').Slideshow | null}
   */
  #getSlideshow() {
    return this.refs.slideshow ?? this.querySelector('slideshow-component.product-full-look-slideshow');
  }
}

if (!customElements.get('product-full-look-section')) {
  customElements.define('product-full-look-section', ProductFullLookSection);
}